import { authStateSchema, type AuthState } from '@vigeo/shared';

export type { AuthState };

export const LOGIN_URL = '/.auth/login/aad?post_login_redirect_uri=/app';
/** Sign-out lands on the public signed-out page, never the candidate portal (#718). */
export const SIGNED_OUT_PATH = '/signed-out';
export const LOGOUT_URL = `/.auth/logout?post_logout_redirect_uri=${SIGNED_OUT_PATH}`;

/**
 * Ask the API who is signed in.
 *
 * The API — not `/.auth/me` — is the authority on what a role means, so the answer
 * comes from there. It always answers 200 with the state in the body, so this never
 * has to infer intent from a status code that Static Web Apps may have rewritten.
 *
 * A non-JSON answer is still treated as signed-out: if the auth layer is misconfigured
 * the platform can serve HTML from any path, and guessing otherwise would strand the
 * user on a blank screen.
 */
export async function fetchAuthState(): Promise<AuthState> {
  const response = await fetch('/api/app/me', {
    headers: { accept: 'application/json' },
    credentials: 'include',
  });

  const isJson = (response.headers.get('content-type') ?? '').includes('application/json');
  if (response.redirected || !isJson) return { status: 'signed-out' };

  if (!response.ok) {
    throw new Error(`Could not load your account (status ${response.status}).`);
  }

  return authStateSchema.parse(await response.json());
}
